import React, { useCallback, useEffect, useRef } from 'react';
import { MousePointerClick, Plus } from 'lucide-react';
import type { ActionButton, ActionButtonModel, VideoFeatures } from '@/store/videoFeatures';
import { switchActionButtonModel } from '@/lib/action-button';
import { MAX_ACTION_BUTTONS, createActionButton } from '@/lib/action-button/list';
import { useActionButtonEditorStore } from '@/store/videoFeatures/actionButtonEditorStore';
import { ActionButtonCard } from './ActionButtonCard';

interface ActionButtonsConfigProps {
  features: VideoFeatures;
  setFeature: <K extends keyof VideoFeatures>(key: K, value: VideoFeatures[K]) => void;
}

/** Botões de ação do vídeo (até 5), um cartão por botão, como o Mini Hook. */
export const ActionButtonsConfig: React.FC<ActionButtonsConfigProps> = ({ features, setFeature }) => {
  const buttons = features.actionButtons ?? [];
  const openId = useActionButtonEditorStore((state) => state.openButtonId);
  const setOpenId = useActionButtonEditorStore((state) => state.setOpenButtonId);

  // O store troca o objeto inteiro a cada setFeature. Sem o ref, duas gravações no mesmo
  // tick (trocar de modelo grava vários campos) partiriam da mesma lista e a segunda
  // apagaria a primeira.
  const buttonsRef = useRef<ActionButton[]>(buttons);
  useEffect(() => {
    buttonsRef.current = features.actionButtons ?? [];
  }, [features.actionButtons]);

  // Abre o 1º botão ao entrar no painel, ou quando a lista chega depois (o painel pode abrir
  // antes de o vídeo carregar). Só uma vez: depois disso, fechar tudo é escolha do usuário.
  const autoOpened = useRef(false);
  useEffect(() => {
    const first = features.actionButtons?.[0];
    if (autoOpened.current || !first) return;
    autoOpened.current = true;
    setOpenId(first.id);
  }, [features.actionButtons, setOpenId]);

  // Ao sair do painel, a prévia volta a seguir só o tempo. Zerar o flag deixa a próxima
  // montagem abrir de novo (o StrictMode do dev monta, desmonta e monta).
  useEffect(
    () => () => {
      autoOpened.current = false;
      setOpenId(null);
    },
    [setOpenId],
  );

  const commit = useCallback(
    (next: ActionButton[]) => {
      buttonsRef.current = next;
      setFeature('actionButtons', next);
    },
    [setFeature],
  );

  const update = useCallback(
    (id: string, change: (button: ActionButton) => ActionButton) => {
      commit(buttonsRef.current.map((button) => (button.id === id ? change(button) : button)));
    },
    [commit],
  );

  const addButton = () => {
    if (buttonsRef.current.length >= MAX_ACTION_BUTTONS) return;
    const button = createActionButton();
    commit([...buttonsRef.current, button]);
    setOpenId(button.id);
  };

  const removeButton = (id: string) => {
    commit(buttonsRef.current.filter((button) => button.id !== id));
    if (openId === id) setOpenId(null);
  };

  const atLimit = buttons.length >= MAX_ACTION_BUTTONS;

  return (
    <div className="space-y-4 py-4 px-6">
      <div className="flex items-center gap-3 rounded-[12px] p-3.5 bg-white border border-[#E4E7EC]">
        <div className="flex-shrink-0 w-9 h-9 rounded-[10px] flex items-center justify-center bg-[#EEF4FF] text-[#1D4FFF]">
          <MousePointerClick className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <h4 className="text-[14px] font-semibold text-[#1A1A1A] leading-tight">Botões de Ação</h4>
          <p className="text-[12px] text-[#6B7280] leading-snug mt-1 line-clamp-2">
            Mostre até {MAX_ACTION_BUTTONS} botões no vídeo, cada um com o próprio tipo, tempo e estilo.
          </p>
        </div>
      </div>

      <div className="space-y-3">
        {buttons.length === 0 && (
          <div className="rounded-xl border border-dashed border-[#E4E7EC] bg-white p-5 text-center">
            <p className="text-[12px] text-[#6B7280]">Nenhum botão adicionado ainda.</p>
          </div>
        )}

        {buttons.map((button, index) => (
          <ActionButtonCard
            key={button.id}
            button={button}
            index={index}
            expanded={openId === button.id}
            onToggleExpanded={() => setOpenId(openId === button.id ? null : button.id)}
            onChange={(key, value) => update(button.id, (current) => ({ ...current, [key]: value }))}
            onTemplateChange={(key, value) =>
              update(button.id, (current) => ({
                ...current,
                template: { ...(current.template || {}), [key]: value } as ActionButton['template'],
              }))
            }
            onSelectModel={(model: ActionButtonModel) =>
              update(button.id, (current) => ({
                ...(switchActionButtonModel(current, model) as ActionButton),
                id: current.id,
              }))
            }
            onRemove={() => removeButton(button.id)}
          />
        ))}

        <button
          type="button"
          onClick={addButton}
          disabled={atLimit}
          className="w-full h-10 rounded-xl border border-dashed border-[#1D4FFF]/40 bg-[#EEF4FF]/40 text-[#1D4FFF] text-[13px] font-semibold flex items-center justify-center gap-2 hover:bg-[#EEF4FF] hover:border-[#1D4FFF] transition-all disabled:opacity-50 disabled:pointer-events-none"
        >
          <Plus className="w-4 h-4" />
          Adicionar botão
        </button>
        {atLimit && <p className="text-[12px] text-[#6B7280] text-center">Limite de {MAX_ACTION_BUTTONS} botões</p>}
      </div>
    </div>
  );
};
